import type { CarAd, MobilityAd } from "@mobility-item-page/types";
import { useRef } from "react";
import { useAdViewAd } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import type { FeatureToggles } from "src/types/unleash.ts";
import { FeatureIds } from "src/types/unleash.ts";
import { useTrackViewOnce } from "src/utils/pulse-tracking/useTrackViewOnce.ts";
import { ChargingCostCard } from "./ChargingCostCard.tsx";
import { EvGuidesCard } from "./EvGuidesCard.tsx";
import { evSectionViewed } from "./evTrackingEvents.ts";
import { RangeAndBatteryCard } from "./RangeAndBatteryCard.tsx";
import { SavingsCard } from "./SavingsCard.tsx";

/**
 * Whether the ad shows the EV info section at all. Also gates the contact form prefill, so a stale
 * `?contact=<topic>` link cannot ask a battery question on an ad that never offered one.
 */
export const showsEvInfoSection = (ad: MobilityAd, featureToggles: FeatureToggles): ad is CarAd =>
  Boolean(featureToggles[FeatureIds.ENABLE_EV_INFO_SECTION]) && "ev_info_links" in ad && Boolean(ad.ev_info_links);

export const EvInfoSection = () => {
  const ad = useAdViewAd();
  const { featureToggles } = useAppConfig();
  const sectionRef = useRef<HTMLDivElement>(null);
  // Tracking event: MO_ev104. Threshold 0 — the section is regularly taller than a phone viewport
  useTrackViewOnce(sectionRef, evSectionViewed(), 0);

  if (!showsEvInfoSection(ad, featureToggles)) {
    return null;
  }

  return (
    // The gap to the key info list below is a bottom margin here rather than a top margin on
    // `.key-info`, because this card is absent on most ads. A top margin on `.key-info` would
    // then stack on top of the specifications area's own `pt-40` and double the gap.
    <div
      ref={sectionRef}
      data-testid="ev-info"
      className="mb-40 p-16 flex flex-col gap-16 s-bg-info-subtle dark:s-bg-subtle -mx-16 md:mx-0 md:rounded-8"
    >
      <RangeAndBatteryCard ad={ad} />
      <SavingsCard ad={ad} />
      <ChargingCostCard ad={ad} />
      <EvGuidesCard ad={ad} />
    </div>
  );
};
